/**
 * state.ts — the build state object and all the pure operations on it:
 * add/remove/move charm, running-total pricing, and serialization to the
 * `_build` JSON shape (spec §5).
 *
 * Pure logic only — NO DOM, NO Shopify. Products are passed in as plain data;
 * this module never fetches anything. The on-screen layer (canvas/drag) and the
 * data layer wire real products into these same shapes later.
 *
 * Money is plain numbers in major currency units (e.g. 12.5 = €12.50). The cart
 * layer converts to Shopify's integer minor units at checkout — not here.
 */

import type { CharmSize } from './geometry';

// ─── Base configuration (spec §3 step 1) ─────────────────────────────────────

export type BaseKind = 'bracelet' | 'necklace' | 'bag_charm' | 'keychain';

export interface BaseConfig {
  kind: BaseKind;
  label: string;
  /** "From" price shown in the base picker. NOT summed into the total — the
   *  chain is the lead priced line item (spec §5). Kept for display only. */
  fromPrice: number;
  maxCharms: number;
  /** bracelet + necklace ship now; the other two are stubbed for easy add-on. */
  enabled: boolean;
}

export const BASE_CONFIG: Record<BaseKind, BaseConfig> = {
  bracelet: { kind: 'bracelet', label: 'Bracelet', fromPrice: 29, maxCharms: 8,  enabled: true  },
  necklace: { kind: 'necklace', label: 'Necklace', fromPrice: 39, maxCharms: 12, enabled: true  },
  bag_charm:{ kind: 'bag_charm',label: 'Bag charm',fromPrice: 19, maxCharms: 5,  enabled: true  },
  // ── stub: add config + assets to ship this; the engine already supports it ──
  keychain: { kind: 'keychain', label: 'Keychain', fromPrice: 15, maxCharms: 4,  enabled: false },
};

/**
 * Canvas layout per base. 'ring' = the circular chain (bracelet/necklace): charms
 * hang around a ring, position stored as an angle t∈[0,1). 'beads' = the bag charm:
 * a fixed beaded-chain base IMAGE with charms attached at hard-pinned coordinate
 * slots (see slots.ts), position stored as a slot index. Drives the canvas renderer
 * (canvas.ts) and the panel tab set (ui.ts).
 */
export type BaseLayout = 'ring' | 'beads';
export const BASE_LAYOUT: Record<BaseKind, BaseLayout> = {
  bracelet: 'ring',
  necklace: 'ring',
  bag_charm: 'beads',
  keychain: 'beads',
};

// ─── Product shapes (what the data layer will supply) ────────────────────────

/**
 * A variant of the chain product. The chain is a live standalone product whose
 * two options — "For" (bracelet/necklace) and "Length" — combine into one variant
 * per combination. The variant is resolved by base + length-tier to get its id and
 * price. Closures are now SEPARATE products (own stock + price), not a chain option.
 *
 * `base` gates the variant to a build type (bracelet vs necklace must never
 * cross). `lengthCm` is the tier label, NOT the manufactured length (see
 * resolveChainPricing).
 */
export interface ChainVariant {
  variantId: string;   // gid://shopify/ProductVariant/... — what the cart charges
  base: BaseKind;      // "For" option → type gate
  lengthCm: number;    // "Length" option → price tier
  price: number;
  inventory: number;
}

/**
 * Optional per-chain length rules (from product metafields, namespace `paopao`). When present
 * for a base, the configurator shows a free DECIMAL length input bounded by [min,max] instead
 * of the tier picker. The necklace additionally has a `base` length: lengths up to `base` are
 * the variant's fixed price; every cm beyond is charged `surchargePerCm` (a separate fee line).
 * Bracelet has no `base` → fixed price across its whole range (the input is just the cut size).
 */
export interface LengthRules {
  bracelet: { min: number; max: number } | null;
  necklace: { min: number; max: number; base: number } | null;
  surchargePerCm: number;
}

export interface ChainProduct {
  id: string;          // gid://shopify/Product/...
  title: string;
  chainType: string;   // e.g. "Fine box chain, 1.5mm"
  variants: ChainVariant[];
  lengthRules?: LengthRules; // metafield-driven decimal-length + surcharge model (bracelet/necklace)
}

/** The decimal-input length rule for a base (or null → use the tier picker / single variant). */
export function lengthInputRule(
  chain: ChainProduct | null,
  base: BaseKind,
): { min: number; max: number; base?: number } | null {
  const r = chain?.lengthRules;
  if (!r) return null;
  if (base === 'bracelet') return r.bracelet;
  if (base === 'necklace') return r.necklace;
  return null;
}

/**
 * A selected closure (its own product/variant — own stock & price). Lives on the
 * build alongside the chain; adds a separate cart line and its price to the total.
 */
export interface ClosureSelection {
  id: string;          // gid://shopify/Product/... (the closure product)
  variantId: string;   // gid://shopify/ProductVariant/... — what the cart charges
  title: string;       // denormalized for fulfillment
  price: number;
}

/** The chain's variants available to a build of `base` (bracelet vs necklace). */
export function variantsForBase(chain: ChainProduct | null, base: BaseKind): ChainVariant[] {
  return chain ? chain.variants.filter(v => v.base === base) : [];
}

/**
 * Per-base typeable floor (cm). Bracelet floors at a hard 10cm — people measure
 * their wrist, so we allow below the shortest tier and let the price fall back to
 * that tier (resolveChainPricing). Other bases (e.g. necklace) get no such offset,
 * so they floor at their own shortest chain variant.
 */
export const LENGTH_FLOOR: Partial<Record<BaseKind, number>> = { bracelet: 10 };

/**
 * The valid/typeable range for a base: floor at LENGTH_FLOOR[base] (or the shortest
 * variant of that base when no floor is set), ceiling at the LONGEST variant length.
 * The number input is bounded to this, so out-of-range lengths can't be entered.
 */
export function lengthBounds(chain: ChainProduct | null, base: BaseKind): { min: number; max: number } | null {
  // Metafield decimal-input model (bracelet/necklace) wins: bounds come straight from the rule.
  const rule = lengthInputRule(chain, base);
  if (rule) return { min: rule.min, max: rule.max };
  // Otherwise: real length tiers (> 0). A palette with no Length option (e.g. a single-size
  // keychain) has none → no bounds, so length-driven UI/defaults are skipped.
  const lens = variantsForBase(chain, base).map(v => v.lengthCm).filter(l => l > 0);
  if (lens.length === 0) return null;
  return { min: LENGTH_FLOOR[base] ?? Math.min(...lens), max: Math.max(...lens) };
}

/**
 * Length surcharge (€) for the current build: necklaces are charged `surchargePerCm` for every
 * cm beyond the base length; everything at/under base (and all bracelets) is 0. Added on top of
 * the variant price in the running total, and charged at checkout via a separate fee line.
 */
export function lengthSurcharge(b: BuildState): number {
  const rules = b.chain?.lengthRules;
  const nk = rules?.necklace;
  if (b.base !== 'necklace' || !nk || b.length == null) return 0;
  const over = b.length - nk.base;
  return over > 0 ? over * (rules?.surchargePerCm || 0) : 0;
}

/**
 * Pre-filled length (cm) for a fresh build: an average wrist circumference for the
 * bracelet, so the field isn't sitting at the bare minimum. Other bases fall back to
 * their shortest tier. Always clamped to the base's valid range.
 */
export const DEFAULT_LENGTH: Partial<Record<BaseKind, number>> = { bracelet: 19, necklace: 55 };

export function defaultLength(
  base: BaseKind,
  bounds: { min: number; max: number } | null,
): number | null {
  if (!bounds) return null;
  const want = DEFAULT_LENGTH[base];
  if (want == null) return bounds.min;
  return Math.min(Math.max(want, bounds.min), bounds.max);
}

export interface CharmProduct {
  productId: string;
  title: string;       // denormalized so fulfillment reads names, not IDs
  price: number;
  size: CharmSize;
}

// ─── Build state ─────────────────────────────────────────────────────────────

/**
 * A charm instance in a build. `uid` is a per-instance id (the same charm
 * product can appear multiple times). `t` is its ring position, or `null` while
 * it sits unplaced in the parking tray — tray charms are not priced/serialized.
 */
export interface CharmInstance {
  uid: string;
  productId: string;
  title: string;
  price: number;
  size: CharmSize;
  t: number | null;
}

export interface BuildState {
  groupId: string;
  base: BaseKind;
  chain: ChainProduct | null;
  length: number | null;            // cm, decimals allowed (spec §1)
  closure: ClosureSelection | null; // selected closure product (separate line + price)
  charms: CharmInstance[];
}

// ─── id helpers ──────────────────────────────────────────────────────────────

function rid(n = 6): string {
  return Math.random().toString(36).slice(2, 2 + n);
}

/** `build_<short>` — stamped on every cart line item of the build (spec §5). */
export function newGroupId(): string {
  return `build_${rid(6)}`;
}

let uidSeq = 0;
function newUid(): string {
  return `charm_${(uidSeq++).toString(36)}_${rid(4)}`;
}

// ─── Construction & top-level setters ────────────────────────────────────────

export function createBuild(base: BaseKind = 'bracelet'): BuildState {
  return {
    groupId: newGroupId(),
    base,
    chain: null,
    length: null,
    closure: null,
    charms: [],
  };
}

export function setBase(b: BuildState, base: BaseKind): void {
  b.base = base;
}

/** Override the base defaults with the section-authored values (label / max charms /
 *  from price) so maxCharms() and the pricing read the live config. Called once on load. */
export function configureBases(
  bases: { kind: BaseKind; label: string; maxCharms: number; fromPrice: number }[],
): void {
  for (const b of bases) {
    const cfg = BASE_CONFIG[b.kind];
    if (!cfg) continue;
    cfg.label = b.label || cfg.label;
    cfg.maxCharms = b.maxCharms || cfg.maxCharms;
    cfg.fromPrice = b.fromPrice || cfg.fromPrice;
    cfg.enabled = true;
  }
}

export function setChain(b: BuildState, chain: ChainProduct | null): void {
  b.chain = chain;
}

export function setClosure(b: BuildState, closure: ClosureSelection | null): void {
  b.closure = closure;
}

export interface LengthResult {
  ok: boolean;
  message?: string;
}

/**
 * Set the chain length in cm (the real, delivered spec — decimals allowed).
 * Validates against the selected base's variant range [min, max]; on rejection
 * the previous length is kept and a message is returned for the UI.
 */
export function setLength(b: BuildState, length: number | null): LengthResult {
  if (length === null) { b.length = null; return { ok: true }; }
  if (!Number.isFinite(length) || length <= 0) {
    return { ok: false, message: 'Enter a length in cm.' };
  }
  const bounds = lengthBounds(b.chain, b.base);
  if (bounds) {
    const label = BASE_CONFIG[b.base].label;
    if (length < bounds.min) return { ok: false, message: `Min ${bounds.min}cm for ${label}.` };
    if (length > bounds.max) return { ok: false, message: `Max ${bounds.max}cm for ${label}.` };
  }
  b.length = length;
  return { ok: true };
}

// ─── Charm add / remove / move ───────────────────────────────────────────────

export function maxCharms(b: BuildState): number {
  return BASE_CONFIG[b.base].maxCharms;
}

/** Charm instances currently ON the ring (the priced/serialized set). */
export function ringCharms(b: BuildState): CharmInstance[] {
  return b.charms.filter(c => c.t !== null);
}

export function ringCount(b: BuildState): number {
  return ringCharms(b).length;
}

/** True when the ring is at the base's max charm count (spec §2). */
export function isAtMax(b: BuildState): boolean {
  return ringCount(b) >= maxCharms(b);
}

export function findCharm(b: BuildState, uid: string): CharmInstance | undefined {
  return b.charms.find(c => c.uid === uid);
}

/**
 * How many instances of a charm product are currently in the build — counting BOTH
 * tray and ring charms, since every staged instance is destined for the order and
 * must not exceed live stock. The UI compares this against the charm's `available`
 * inventory to cap how many of a charm can be added.
 */
export function countCharm(b: BuildState, productId: string): number {
  return b.charms.reduce((n, c) => (c.productId === productId ? n + 1 : n), 0);
}

/**
 * Add a charm to the build. With `t === null` it lands in the parking tray
 * (always allowed). With a `t` it goes straight onto the ring, subject to the
 * max-charm limit. Returns the new uid, or `null` if a ring placement was
 * blocked by the max.
 */
export function addCharm(b: BuildState, product: CharmProduct, t: number | null = null): string | null {
  if (t !== null && isAtMax(b)) return null;
  const uid = newUid();
  b.charms.push({ uid, productId: product.productId, title: product.title, price: product.price, size: product.size, t });
  return uid;
}

export function removeCharm(b: BuildState, uid: string): void {
  const i = b.charms.findIndex(c => c.uid === uid);
  if (i !== -1) b.charms.splice(i, 1);
}

/**
 * Place (or reposition) a charm on the ring at `t`. Repositioning an
 * already-placed charm is always allowed; moving a tray charm onto the ring is
 * blocked when at max. Caller resolves `t` via geometry.nudgeToFreeSlot first.
 * Returns true on success.
 */
export function placeCharm(b: BuildState, uid: string, t: number): boolean {
  const c = findCharm(b, uid);
  if (!c) return false;
  const wasPlaced = c.t !== null;
  if (!wasPlaced && isAtMax(b)) return false;
  c.t = t;
  return true;
}

/** Send a charm back to the parking tray (unplaced, unpriced). */
export function parkCharm(b: BuildState, uid: string): void {
  const c = findCharm(b, uid);
  if (c) c.t = null;
}

// ─── Chain length → price tier (the variant is for pricing ONLY) ─────────────

export interface ChainPricing {
  variant: ChainVariant;        // resolved variant (its variantId charges natively)
  typedLengthCm: number;        // the real spec the customer typed
  pricedAtCm: number;           // variant.lengthCm — the tier, NOT manufactured
  price: number;
  capped: boolean;              // typed exceeded the longest variant
}

/**
 * Resolve a build to its exact chain variant — matched on base, then length-tier.
 * Only variants of the build's `base` are considered (bracelet never crosses into
 * necklace). Round UP to the first variant whose length ≥ typed (e.g. 19→20 tier,
 * 20→20 exact).
 * - Below the smallest variant: smallest variant's price (defensive floor).
 * - Above the largest variant: capped to the top variant (defensive; input bounded).
 * Returns null when there's no chain/eligible variants or the length is unset/invalid.
 *
 * The variant's length is ONLY a price tier — the delivered length is the typed
 * value (stored in _build.length / _chain_length_cm), never the variant length.
 */
export function resolveChainPricing(
  chain: ChainProduct | null,
  base: BaseKind,
  typedLengthCm: number | null,
): ChainPricing | null {
  const vs = variantsForBase(chain, base);
  if (vs.length === 0) return null;

  const sorted = [...vs].sort((a, b) => a.lengthCm - b.lengthCm);
  // Length-less palette (e.g. keychain: a single "For" variant, no Length option): there's no
  // tier to pick, so resolve straight to its variant regardless of typed length.
  if (!sorted.some(v => v.lengthCm > 0)) {
    const variant = sorted[0];
    return { variant, typedLengthCm: variant.lengthCm, pricedAtCm: variant.lengthCm, price: variant.price, capped: false };
  }

  if (typedLengthCm === null || !Number.isFinite(typedLengthCm) || typedLengthCm <= 0) return null;
  let variant = sorted.find(v => v.lengthCm >= typedLengthCm);
  let capped = false;
  if (!variant) { variant = sorted[sorted.length - 1]; capped = true; } // exceeds longest → cap

  return { variant, typedLengthCm, pricedAtCm: variant.lengthCm, price: variant.price, capped };
}

/** The chain's contribution to the total: the resolved variant price (0 if unresolved). */
export function chainPrice(b: BuildState): number {
  return resolveChainPricing(b.chain, b.base, b.length)?.price ?? 0;
}

/** How many closures a base uses: a bag charm has a carabiner at BOTH ends (2), others 1. */
export function closureQty(base: BaseKind): number {
  return base === 'bag_charm' ? 2 : 1;
}

/** The selected closure's contribution to the total (0 when none selected). */
export function closurePrice(b: BuildState): number {
  return (b.closure?.price ?? 0) * closureQty(b.base);
}

/**
 * Human-readable length, making the tier unambiguous:
 * "20cm" (exact) | "19cm (priced at 20cm tier)".
 */
export function chainLengthSummary(b: BuildState): string | null {
  if (b.length === null) return null;
  const p = resolveChainPricing(b.chain, b.base, b.length);
  if (!p) return `${b.length}cm`;
  if (p.pricedAtCm === b.length) return `${b.length}cm`;
  return `${b.length}cm (priced at ${p.pricedAtCm}cm tier${p.capped ? ' · max' : ''})`;
}

export interface ResolvedChainLine {
  variantId: string;       // cart charges this → native variant (tier) price
  realLengthCm: number;    // → _chain_length_cm line item property (delivered spec)
  pricedAtCm: number;      // fulfillment note: the price tier, not the made length
  price: number;
  capped: boolean;
}

/** Everything cart.ts needs for the chain line item (variant = price, real = spec). */
export function resolveChainLine(b: BuildState): ResolvedChainLine | null {
  const p = resolveChainPricing(b.chain, b.base, b.length);
  if (!p) return null;
  return {
    variantId: p.variant.variantId,
    realLengthCm: p.typedLengthCm,
    pricedAtCm: p.pricedAtCm,
    price: p.price,
    capped: p.capped,
  };
}

export interface ResolvedClosureLine {
  variantId: string;
  title: string;
  price: number;
}

/** The closure's cart line (separate product), or null when none is selected. */
export function resolveClosureLine(b: BuildState): ResolvedClosureLine | null {
  if (!b.closure) return null;
  return { variantId: b.closure.variantId, title: b.closure.title, price: b.closure.price };
}

// ─── Pricing (spec §9) ───────────────────────────────────────────────────────

/** Σ(charm price) over ring-placed charms only. */
export function charmsSubtotal(b: BuildState): number {
  return ringCharms(b).reduce((sum, c) => sum + c.price, 0);
}

/**
 * Running total = chain variant price (length tier) + selected closure price +
 * Σ(ring charm prices). The closure is its own tracked product/line now.
 */
export function runningTotal(b: BuildState): number {
  return chainPrice(b) + closurePrice(b) + charmsSubtotal(b) + lengthSurcharge(b);
}

// ─── Review aggregation (spec §9 — group by type) ────────────────────────────

export interface ChargeLine {
  productId: string;
  title: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
}

/**
 * Aggregate ring charms by productId for the review screen: one line per type
 * with its quantity and per-type total (e.g. "Crying Cloud · 3 Pieces · €36").
 * Insertion order follows first appearance.
 */
export function aggregateCharms(b: BuildState): ChargeLine[] {
  const byProduct = new Map<string, ChargeLine>();
  for (const c of ringCharms(b)) {
    const line = byProduct.get(c.productId);
    if (line) {
      line.quantity += 1;
      line.lineTotal += c.price;
    } else {
      byProduct.set(c.productId, {
        productId: c.productId,
        title: c.title,
        unitPrice: c.price,
        quantity: 1,
        lineTotal: c.price,
      });
    }
  }
  return [...byProduct.values()];
}

/**
 * A stable identity string for a build's RING content — base + chain + closure +
 * length + the placed charms (productId@t, sorted). Two builds that look identical
 * produce the same signature. Drives favorite dedup (no doubles) and the
 * dirty-since-saved check that gates the leave-guard. Tray-only charms are ignored
 * (they're not part of the piece yet).
 */
export function buildSignature(b: BuildState): string {
  return JSON.stringify({
    base: b.base,
    chain: b.chain?.id ?? null,
    closure: b.closure?.variantId ?? null,
    length: b.length,
    charms: ringCharms(b)
      .map(c => `${c.productId}@${(c.t as number).toFixed(3)}`)
      .sort(),
  });
}

// ─── Serialization to the `_build` JSON (spec §5) ────────────────────────────

export interface BuildCharmJSON {
  productId: string;
  title: string;
  t: number;
}

export interface BuildJSON {
  groupId: string;
  base: BaseKind;
  chainId: string | null;
  chainTitle: string | null;
  chainType: string | null;
  chainVariantId: string | null;  // resolved variant (length tier) — what the cart charges
  length: number | null;          // the real delivered spec
  closure: ClosureSelection | null; // chosen closure product (separate line + price)
  charms: BuildCharmJSON[];
  layoutImage: string;
}

/**
 * Serialize to the lead line item's `_build` JSON — the source of truth for
 * re-validation and fulfillment. Only ring-placed charms are included, sorted
 * by `t` (closure-clockwise). `title` is denormalized per charm so fulfillment
 * reads names, not IDs. `layoutImage` defaults to "unavailable" (spec §7: image
 * generation never blocks the build).
 */
export function serializeBuild(b: BuildState, layoutImage = 'unavailable'): BuildJSON {
  return {
    groupId: b.groupId,
    base: b.base,
    chainId: b.chain?.id ?? null,
    chainTitle: b.chain?.title ?? null,
    chainType: b.chain?.chainType ?? null,
    chainVariantId: resolveChainLine(b)?.variantId ?? null,
    length: b.length,
    closure: b.closure,
    charms: ringCharms(b)
      .slice()
      .sort((a, c) => (a.t as number) - (c.t as number))
      .map(c => ({ productId: c.productId, title: c.title, t: c.t as number })),
    layoutImage,
  };
}
